import { useProductDetails } from "./useProductDetails";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  updateCoolingRegimeProductTemperatures,
  updateCoolingRegimeProductPriorities,
  type UpdateCoolingRegimeProductTemperaturesPayload,
  type UpdateCoolingRegimeProductPrioritiesPayload,
} from "../services/coolingRegimeProductService";
import { fetchAllCoolingRegimeTemperatures } from "../services/coolingRegimeTemperatureService";
import { fetchAllCoolingRegimePriorities } from "../services/coolingRegimePriorityService";
import { deleteCoolingRegimeProduct } from "../services/coolingRegimeProductService";
import { upsertCoolingRegimeProduct } from "../services/coolingRegimeProductService";
import type { UpsertCommand2 } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";

export const useProductCoolingRegime = (productId: string) => {
  const {
    data: productDetails,
    isLoading,
    isError,
  } = useProductDetails(productId);

  return {
    data: productDetails?.coolingRegimeProducts ?? [],
    isLoading,
    isError,
  };
};

/**
 * Hook to fetch all cooling regime temperatures for dropdown options
 */
export function useCoolingRegimeTemperatureOptions() {
  return useQuery({
    queryKey: ["coolingRegimeTemperatureOptions"],
    queryFn: fetchAllCoolingRegimeTemperatures,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/**
 * Hook to fetch all cooling regime priorities for dropdown options
 */
export function useCoolingRegimePriorityOptions() {
  return useQuery({
    queryKey: ["coolingRegimePriorityOptions"],
    queryFn: fetchAllCoolingRegimePriorities,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/**
 * Hook to update cooling regime product temperatures
 */
export function useUpdateCoolingRegimeProductTemperatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoolingRegimeProductTemperatures,
    onSuccess: (data) => {
      // Invalidate product details to refresh cooling regime data
      queryClient.invalidateQueries({
        queryKey: ["productDetails", data.productCode?.toString()],
      });
    },
  });
}

/**
 * Hook to update cooling regime product priorities
 */
export function useUpdateCoolingRegimeProductPriorities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoolingRegimeProductPriorities,
    onSuccess: (data) => {
      // Invalidate product details to refresh cooling regime data
      queryClient.invalidateQueries({
        queryKey: ["productDetails", data.productCode?.toString()],
      });
    },
  });
}

/**
 * Hook to delete a cooling regime product
 */
export function useDeleteCoolingRegimeProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCoolingRegimeProduct,
    onSuccess: (_data, variables) => {
      // Invalidate any productDetails queries; productCode is not available here
      queryClient.invalidateQueries({ queryKey: ["productDetails"] });
      // Optionally, we could invalidate coolingRegime lists if keyed separately
    },
  });
}

/**
 * Hook to create/upsert a cooling regime product
 */
export function useCreateCoolingRegimeProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertCommand2) => upsertCoolingRegimeProduct(payload),
    onSuccess: (data) => {
      // Invalidate specific product details if productCode known, else fallback to broad invalidation
      const key = data.productCode?.toString();
      if (key) {
        queryClient.invalidateQueries({ queryKey: ["productDetails", key] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["productDetails"] });
      }
    },
  });
}
